import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  TrendingUp,
  Wallet,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Plus,
  Minus,
  Share2,
  Bookmark,
  Zap,
  Sparkles,
  BadgeCheck,
  AlertCircle,
  RefreshCw,
  Building2,
  Calendar,
  ShieldAlert,
  Users,
  Briefcase,
  ExternalLink,
  Layers,
  Scale,
  Factory,
  Globe2,
  PieChart,
  Award,
} from 'lucide-react';
import { fetchIpoDetails, fetchIpoFaqs, clearCurrentIpo } from '../../store/slices/ipoSlice';
import { mapIpoDetailResponse } from '../../utils/ipoDataMapper';
import CompanyLogo from '../common/CompanyLogo';
import CompanyNewsPanel from './CompanyNewsPanel';
import AdSlot from '../advertisement/AdSlot';
import OfferStrip from '../common/OfferStrip';

export const IpoDetailsPage = ({ isSme = false }) => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    currentIpo,
    currentIpoLoading,
    currentIpoError,
    currentIpoFaqs,
    currentIpoFaqsLoading,
  } = useSelector((state) => state.ipo);

  // Active Tab state: 'overview' | 'about' | 'gmp' | 'subscription' | 'financials' | 'news'
  const [activeTab, setActiveTab] = useState('overview');

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Quick Bid Calculator Interactive State
  const [lots, setLots] = useState(1);

  // Fetch IPO Details and its FAQs from API
  useEffect(() => {
    if (id) {
      dispatch(fetchIpoDetails(id));
      dispatch(fetchIpoFaqs(id));
      setOpenFaqIndex(0);
    }
    return () => {
      dispatch(clearCurrentIpo());
    };
  }, [id, dispatch]);

  // Normalize data with mapIpoDetailResponse
  const ipo = useMemo(() => {
    return mapIpoDetailResponse(currentIpo);
  }, [currentIpo]);

  // Dynamic calculator values
  const lotSize = ipo?.lotSize || 100;
  const cutOffPrice = ipo?.upperPrice || ipo?.floorPrice || 100;
  const totalShares = lots * lotSize;
  const totalPayable = lots * lotSize * cutOffPrice;

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'about', label: 'About & Peers' },
    {
      id: 'gmp',
      label: 'GMP Trend',
      pill: ipo?.liveGmp ? 'LIVE' : '',
      pillColor: 'bg-emerald-100 text-emerald-800',
    },
    {
      id: 'subscription',
      label: 'Subscription',
      pill: ipo?.overallSubscription ? ipo.overallSubscription : '',
      pillColor: 'bg-teal-100 text-[#008080]',
    },
    { id: 'financials', label: 'Financials & Valuation' },
    {
      id: 'news',
      label: 'News',
      pill: ipo?.companyNews?.length ? String(ipo.companyNews.length) : '',
      pillColor: 'bg-slate-100 text-slate-600',
    },
  ];

  // Fallback FAQs built from the IPO record, used only if GET /api/core/ipos/{id}/faqs fails or is empty
  const companyFaqs = useMemo(() => {
    const cName = ipo?.companyName || 'this company';
    return [
      {
        question: `How to apply for ${cName} IPO?`,
        answer: `You can apply for ${cName} IPO using UPI via your trading app (Zerodha, Groww, AngelOne, Upstox) or directly through net-banking ASBA facility provided by all major banks in India.`,
      },
      {
        question: `What is the price band and lot size of ${cName}?`,
        answer: ipo?.priceBand
          ? `The issue price band is set at ${ipo.priceBand} with a minimum lot size of ${ipo.lotSize ? `${ipo.lotSize} shares` : 'designated lots'}.`
          : `The price band for ${cName} will be updated once officially published in the prospectus.`,
      },
      {
        question: `What is the estimated Grey Market Premium (GMP) for ${cName}?`,
        answer: ipo?.liveGmp
          ? `As of the latest market intelligence, the GMP is estimated around ${ipo.liveGmp} (${ipo.gmpPercent || ''}), with estimated listing around ${ipo.estimatedListingPrice || 'TBD'}.`
          : `Grey Market Premium data for ${cName} is being tracked and will reflect once trading quotes are active.`,
      },
      {
        question: `When does ${cName} IPO open and close for bidding?`,
        answer: ipo?.openDate && ipo?.closeDate
          ? `The bidding opens on ${ipo.openDate} and closes on ${ipo.closeDate} at 5:00 PM.`
          : `The bidding schedule will be announced in accordance with exchange timelines.`,
      },
    ];
  }, [ipo]);

  const faqs = currentIpoFaqs.length ? currentIpoFaqs : companyFaqs;
  const faqsLoading = currentIpoFaqsLoading && !currentIpoFaqs.length;

  // Loading Skeleton State
  if (currentIpoLoading) {
    return (
      <div className="min-h-screen bg-slate-50/60 font-sans text-slate-900 pb-16">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-8 space-y-6">
          <div className="h-4 w-48 bg-slate-200 rounded animate-pulse" />
          <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 space-y-4 animate-pulse">
            <div className="flex gap-2">
              <div className="h-6 w-20 bg-slate-200 rounded-full" />
              <div className="h-6 w-24 bg-slate-200 rounded-full" />
            </div>
            <div className="h-10 w-2/3 bg-slate-300 rounded" />
            <div className="h-4 w-1/2 bg-slate-200 rounded" />
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-6 border-t border-slate-100">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-slate-100 rounded-2xl" />
              ))}
            </div>
          </div>
          <div className="h-12 w-full bg-slate-200 rounded-2xl animate-pulse" />
          <div className="h-96 w-full bg-white rounded-3xl border border-slate-200 animate-pulse" />
        </div>
      </div>
    );
  }

  // Error / Not Found State
  if (currentIpoError || (!currentIpoLoading && !ipo)) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 p-8 text-center shadow-sm space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto text-amber-600">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900">
            IPO Details Not Available
          </h2>
          <p className="text-xs text-slate-500">
            {currentIpoError || `No records found for IPO ID #${id}.`}
          </p>
          <div className="pt-2 flex items-center justify-center gap-3">
            <Link
              to="/"
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all"
            >
              ← Back to IPO List
            </Link>
            <button
              type="button"
              onClick={() => dispatch(fetchIpoDetails(id))}
              className="px-5 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-white text-xs font-bold transition-all inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60 font-sans text-slate-900 pb-16">
      {/* Partner ad (GET /api/ads): IPO detail slot first, then the home leaderboard */}
      <OfferStrip slotId={['ipo-detail-top', 'leaderboard']} />

      {/* Main Container */}
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-4 sm:pt-6">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-4 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-[#008080] transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <Link to="/" className="hover:text-[#008080] transition-colors">
            IPOs
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="hover:text-[#008080] cursor-pointer">
            {isSme ? 'SME IPOs' : 'Mainboard IPOs'}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-900 font-semibold truncate">
            {ipo.companyName}
          </span>
        </nav>

        {/* Company Header Card */}
        <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-7 shadow-xs mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            {/* Left: Avatar/Logo + Tags + Company Title */}
            <div className="flex items-start gap-4">
              <CompanyLogo
                company={ipo.companyName}
                logoUrl={ipo.logoUrl}
                size="lg"
                maxInitials={1}
                fallbackClassName="bg-gradient-to-tr from-[#006666] via-[#008080] to-[#2dd4bf] text-white shadow-md shadow-[#008080]/20"
              />

              <div className="space-y-2">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2">
                  {ipo.type && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-teal-50 text-[#008080] border border-teal-200/80">
                      {ipo.type}
                    </span>
                  )}
                  {ipo.biddingStatus && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span>{ipo.biddingStatus}</span>
                    </span>
                  )}
                  {ipo.exchanges && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                      {ipo.exchanges} Listed
                    </span>
                  )}
                  {ipo.symbol && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-mono font-bold bg-slate-100 text-slate-800 border border-slate-200">
                      {ipo.symbol}
                    </span>
                  )}
                  {ipo.cin && (
                    <span className="text-[11px] text-slate-400 font-mono hidden md:inline">
                      CIN: {ipo.cin}
                    </span>
                  )}
                </div>

                {/* Title & Tagline */}
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                    {ipo.companyName}
                  </h1>
                  {ipo.tagline && (
                    <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">
                      {ipo.tagline}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            {/* <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#008080] via-[#008080] to-[#006666] hover:from-[#009696] hover:to-[#008080] text-white font-bold text-sm shadow-md shadow-[#008080]/30 hover:shadow-[#008080]/40 transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                <Zap className="w-4 h-4 fill-white" />
                <span>Apply via UPI / ASBA</span>
              </button>

              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-all"
              >
                <BadgeCheck className="w-4 h-4 text-[#008080]" />
                <span>Check Allotment</span>
              </button>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Share"
                  className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="Bookmark"
                  className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <Bookmark className="w-4 h-4" />
                </button>
              </div>
            </div> */}
          </div>

          {/* Key Metrics Strip (5 Cards) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-6 pt-6 border-t border-slate-100">
            {/* Metric 1 */}
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-50/80 border border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Issue Price Band
              </div>
              <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                {ipo.priceBand || '—'}
              </div>
              <div className="text-[11px] text-slate-500 font-medium mt-0.5">
                {ipo.faceValue ? `Face Value: ${ipo.faceValue}` : ''}
              </div>
            </div>

            {/* Metric 2 */}
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-50/80 border border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Total Issue Size
              </div>
              <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                {ipo.totalIssueSize || '—'}
              </div>
              <div className="text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                {ipo.freshIssue ? `Fresh: ${ipo.freshIssue}` : ''}
              </div>
            </div>

            {/* Metric 3 */}
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-50/80 border border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Lot Size / Min Bid
              </div>
              <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                {ipo.lotSize ? `${ipo.lotSize} Shares` : '—'}
              </div>
              <div className="text-[11px] text-slate-500 font-medium mt-0.5">
                {ipo.minBidAmount ? `${ipo.minBidAmount} at cap` : ''}
              </div>
            </div>

            {/* Metric 4 */}
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-50/80 border border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Tentative Listing
              </div>
              <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                {ipo.tentativeListing || '—'}
              </div>
              <div className="text-[11px] text-[#008080] font-medium mt-0.5">
                {ipo.closeDateText}
              </div>
            </div>

            {/* Metric 5: Live GMP */}
            <div className="col-span-2 sm:col-span-1 p-3 sm:p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/70">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                  Live GMP Estimate
                </span>
                {ipo.gmpStatus && (
                  <span className="text-[10px] font-extrabold px-1.5 py-0.2 rounded bg-emerald-600 text-white">
                    {ipo.gmpStatus}
                  </span>
                )}
              </div>
              <div className="text-base sm:text-lg font-extrabold text-emerald-700 mt-1 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span>{ipo.liveGmp || '—'}</span>
                {ipo.gmpPercent && (
                  <span className="text-xs font-semibold text-emerald-600 ml-1">
                    ({ipo.gmpPercent})
                  </span>
                )}
              </div>
              <div className="text-[11px] text-emerald-800 font-medium mt-0.5">
                {ipo.estimatedListingPrice
                  ? `Est. Listing: ${ipo.estimatedListingPrice}`
                  : ''}
              </div>
            </div>
          </div>
        </div>

        {/* All 5 Tabs Navigation Strip */}
        <div className="flex items-center gap-2 border-b border-slate-200 mb-6 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${isActive
                  ? 'border-[#008080] text-[#008080] bg-white rounded-t-xl'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
                  }`}
              >
                <span>{tab.label}</span>
                {tab.pill && (
                  <span
                    className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${tab.pillColor}`}
                  >
                    {tab.pill}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ========================================================================= */}
        {/* TAB 1: OVERVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Timeline & Dates Card */}
            {ipo.datesTimeline && ipo.datesTimeline.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-xs">
                <h3 className="font-extrabold text-base sm:text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#008080]" />
                  <span>Important Issue Dates &amp; Milestones</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {ipo.datesTimeline.map((milestone, idx) => (
                    <div
                      key={idx}
                      className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/70 relative overflow-hidden"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#008080] mb-2" />
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                        {milestone.label}
                      </div>
                      <div className="text-sm sm:text-base font-extrabold text-slate-900 mt-1">
                        {milestone.date}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {milestone.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issue Structure & Capital Details */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: IPO Structure Breakdown */}
              <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-xs space-y-4">
                <h3 className="font-extrabold text-base sm:text-lg text-slate-900 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#008080]" />
                  <span>IPO Structure &amp; Share Capital</span>
                </h3>
                <div className="divide-y divide-slate-100 text-xs sm:text-sm">
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Issue Type</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.issueType || 'Book Built Issue'}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Face Value</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.faceValue || '—'}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Fresh Issue</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.freshIssue || '—'}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Offer for Sale (OFS)</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.ofs || 'NIL'}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Total Shares Offered</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.sharesOffered || ipo.raw?.json_data?.ipo_structure?.total_issue_size_shares || '—'}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between">
                    <span className="text-slate-500">Listing At</span>
                    <span className="font-semibold text-slate-900">
                      {ipo.exchanges || 'NSE, BSE'}
                    </span>
                  </div>
                </div>

                {/* Capital Structure Breakdown */}
                {(ipo.authorisedCapital || ipo.preIssueShares || ipo.postIssueShares) && (
                  <div className="pt-4 border-t border-slate-100 space-y-2">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                      Capital Structure
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      {ipo.authorisedCapital && (
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center">
                          <span className="text-slate-500">Authorised Capital</span>
                          <span className="font-semibold text-slate-900">₹{ipo.authorisedCapital}</span>
                        </div>
                      )}
                      {ipo.sharesOffered && (
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center">
                          <span className="text-slate-500">Shares in Issue</span>
                          <span className="font-semibold text-slate-900">{ipo.sharesOffered}</span>
                        </div>
                      )}
                      {ipo.preIssueShares && (
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center">
                          <span className="text-slate-500">Pre-Issue Shares</span>
                          <span className="font-semibold text-slate-900">{ipo.preIssueShares}</span>
                        </div>
                      )}
                      {ipo.postIssueShares && (
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center">
                          <span className="text-slate-500">Post-Issue Shares</span>
                          <span className="font-semibold text-slate-900">{ipo.postIssueShares}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Objects of the Issue */}
                {ipo.objectsList && ipo.objectsList.length > 0 && (
                  <div className="pt-4 border-t border-slate-100 space-y-2">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                      Objects of the Issue (Utilisation)
                    </h4>
                    <div className="space-y-2">
                      {ipo.objectsList.map((obj, idx) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs"
                        >
                          <span className="text-slate-700 font-medium">
                            {idx + 1}. {obj.object}
                          </span>
                          {obj.amount && (
                            <span className="font-bold text-slate-900 shrink-0">
                              {obj.amount}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Bid Calculator & Investor Quota */}
              <div className="lg:col-span-5 space-y-6">
                {/* Fast Bid Calculator Card */}
                <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-xs space-y-4">
                  <h3 className="font-extrabold text-base text-slate-900 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[#008080]" />
                    <span>Quick Bid Calculator</span>
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">Number of Lots</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setLots(Math.max(1, lots - 1))}
                          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 font-bold"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="font-extrabold text-sm px-2">
                          {lots}
                        </span>
                        <button
                          type="button"
                          onClick={() => setLots(lots + 1)}
                          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 font-bold"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl bg-teal-50/60 border border-teal-100 space-y-1 text-xs">
                      <div className="flex justify-between text-slate-600">
                        <span>Total Shares ({lotSize} / lot)</span>
                        <span className="font-bold text-slate-900">
                          {totalShares} Shares
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Cut-off Price</span>
                        <span className="font-bold text-slate-900">
                          ₹{cutOffPrice}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-teal-200/60 flex justify-between items-center text-sm font-extrabold text-teal-950">
                        <span>Total Application Amount</span>
                        <span>₹{totalPayable.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Investor Quota / Reservation Card */}
                <div className="bg-white rounded-3xl border border-slate-200/90 p-5 shadow-xs space-y-3">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                    Investor Reservation Quota
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">
                        QIB
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                        {ipo.reservation?.qibMax ? `${ipo.reservation.qibMax}%` : '50%'}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">
                        NII / HNI
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                        {ipo.reservation?.niiMin ? `${ipo.reservation.niiMin}%` : '15%'}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">
                        Retail
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                        {ipo.reservation?.retailMin ? `${ipo.reservation.retailMin}%` : '35%'}
                      </div>
                    </div>
                  </div>
                  {ipo.reservation?.details && (
                    <p className="text-[11px] text-slate-500 italic mt-1 leading-relaxed">
                      {ipo.reservation.details}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: ABOUT & PEERS */}
        {/* ========================================================================= */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            {/* Company Overview & Model */}
            <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
              <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#008080]" />
                <span>About {ipo.companyName}</span>
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {ipo.companyOverview ||
                  ipo.summaryParagraph ||
                  `${ipo.companyName} operates in the capital market ecosystem.`}
              </p>

              {/* Snapshot Bullets */}
              {ipo.snapshotBullets && ipo.snapshotBullets.length > 0 && (
                <div className="pt-4 border-t border-slate-100 space-y-2">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                    Key Operational Highlights
                  </h4>
                  <ul className="space-y-2">
                    {ipo.snapshotBullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-xs sm:text-sm text-slate-700"
                      >
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Product Categories */}
              {ipo.productCategories && ipo.productCategories.length > 0 && (
                <div className="pt-4 border-t border-slate-100">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-2">
                    Product / Business Categories
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {ipo.productCategories.map((cat, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-xl bg-slate-100 text-slate-700 text-xs font-semibold"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Business Model & Industry Dynamics */}
            {(ipo.businessModel || ipo.industrySummary) && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#008080]" />
                  <span>Business Model &amp; Industry Dynamics</span>
                </h3>
                {ipo.businessModel && (
                  <div className="space-y-1">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                      Revenue &amp; Operations Model
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {ipo.businessModel}
                    </p>
                  </div>
                )}
                {ipo.industrySummary && (
                  <div className="pt-3 border-t border-slate-100 space-y-1">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                      Industry Sector Overview
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {ipo.industrySummary}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Manufacturing & Geographic Presence */}
            {(ipo.manufacturing || ipo.geographicPresence) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {ipo.manufacturing && (
                  <div className="bg-white rounded-3xl border border-slate-200/90 p-6 shadow-xs space-y-2">
                    <h4 className="font-extrabold text-sm text-slate-900 flex items-center gap-2">
                      <Factory className="w-4 h-4 text-[#008080]" />
                      <span>Manufacturing &amp; Facilities</span>
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {ipo.manufacturing}
                    </p>
                  </div>
                )}
                {ipo.geographicPresence && (
                  <div className="bg-white rounded-3xl border border-slate-200/90 p-6 shadow-xs space-y-2">
                    <h4 className="font-extrabold text-sm text-slate-900 flex items-center gap-2">
                      <Globe2 className="w-4 h-4 text-[#008080]" />
                      <span>Geographic Footprint</span>
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {ipo.geographicPresence}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Promoter Shareholding */}
            {ipo.promoterShareholding && ipo.promoterShareholding.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-6 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#008080]" />
                  <span>Promoter Shareholding</span>
                </h3>
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Promoter Name</th>
                        <th className="py-2.5 px-4 text-right">Shares</th>
                        <th className="py-2.5 pl-4 text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.promoterShareholding.map((prom, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-2.5 pr-4 font-semibold text-slate-900">
                            {prom.name}
                          </td>
                          <td className="py-2.5 px-4 text-right text-slate-600">
                            {prom.shares || '—'}
                          </td>
                          <td className="py-2.5 pl-4 text-right font-bold text-emerald-600">
                            {prom.percentage || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Peer Comparison Table */}
            {ipo.peers && ipo.peers.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-6 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#008080]" />
                  <span>Peer Comparison &amp; Industry Valuation</span>
                </h3>
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Company Name</th>
                        <th className="py-2.5 px-4 text-right">P/E Ratio</th>
                        <th className="py-2.5 px-4 text-right">Face Value</th>
                        <th className="py-2.5 pl-4 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.peers.map((peer, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-2.5 pr-4 font-semibold text-slate-900">
                            {peer.company_name || peer.name || 'Peer'}
                          </td>
                          <td className="py-2.5 px-4 text-right font-bold text-slate-800">
                            {peer.pe_ratio || peer.pe || '—'}
                          </td>
                          <td className="py-2.5 px-4 text-right text-slate-600">
                            {peer.face_value ? `₹${peer.face_value}` : '—'}
                          </td>
                          <td className="py-2.5 pl-4 text-right text-slate-600">
                            {peer.revenue ? `₹${peer.revenue} Cr` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Risk Factors */}
            {ipo.topRisks && ipo.topRisks.length > 0 && (
              <div className="bg-white rounded-3xl border border-amber-200/80 p-6 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-amber-900 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-amber-600" />
                  <span>Key Risk Factors (From DRHP / RHP)</span>
                </h3>
                <div className="space-y-2">
                  {ipo.topRisks.slice(0, 5).map((riskText, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-2xl bg-amber-50/50 border border-amber-100 text-xs text-amber-950 flex items-start gap-2.5"
                    >
                      <span className="font-bold text-amber-700 shrink-0">
                        {idx + 1}.
                      </span>
                      <span>{riskText}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Litigations & Legal Proceedings Section */}
            {(ipo.litigationsSummary ||
              (ipo.litigations && ipo.litigations.length > 0) ||
              (ipo.groupCompanies && ipo.groupCompanies.length > 0)) && (
                <div className="bg-white rounded-3xl border border-slate-200/90 p-6 shadow-xs space-y-4">
                  <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-amber-600" />
                    <span>Legal Proceedings, Litigations &amp; Group Entities</span>
                  </h3>
                  {ipo.litigationsSummary && (
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {ipo.litigationsSummary}
                    </p>
                  )}
                  {ipo.litigations && ipo.litigations.length > 0 && (
                    <div className="overflow-x-auto scrollbar-slim">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                            <th className="py-2.5 pr-4">Category</th>
                            <th className="py-2.5 px-3 text-center">Criminal Cases</th>
                            <th className="py-2.5 px-3 text-center">Tax Matters</th>
                            <th className="py-2.5 px-3 text-center">Statutory Claims</th>
                            <th className="py-2.5 pl-4 text-right">Amount (₹ Lakhs)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {ipo.litigations.map((lit, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="py-2.5 pr-4 font-semibold text-slate-800">
                                {lit.category || 'Dispute'}
                              </td>
                              <td className="py-2.5 px-3 text-center text-slate-600">
                                {lit.criminal || '—'}
                              </td>
                              <td className="py-2.5 px-3 text-center text-slate-600">
                                {lit.tax || '—'}
                              </td>
                              <td className="py-2.5 px-3 text-center text-slate-600">
                                {lit.statutory || '—'}
                              </td>
                              <td className="py-2.5 pl-4 text-right font-bold text-amber-700">
                                {lit.amount_lakhs || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {ipo.groupCompanies && ipo.groupCompanies.length > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-2">
                        Group &amp; Affiliate Entities
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {ipo.groupCompanies.map((gc, idx) => (
                          <span
                            key={idx}
                            className="px-2.5 py-1 rounded-xl bg-slate-100 text-slate-600 text-[11px] font-medium"
                          >
                            {gc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Contact, Registrar & Lead Manager */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Registrar */}
              <div className="bg-white rounded-3xl border border-slate-200/90 p-5 space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                  Registrar to the Issue
                </h4>
                <div className="font-extrabold text-sm text-slate-900">
                  {ipo.registrar?.name || 'Registrar details in Prospectus'}
                </div>
                {ipo.registrar?.email && (
                  <div className="text-xs text-slate-600">
                    Email: {ipo.registrar.email}
                  </div>
                )}
                {ipo.registrar?.telephone && (
                  <div className="text-xs text-slate-600">
                    Phone: {ipo.registrar.telephone}
                  </div>
                )}
                {ipo.registrar?.website && (
                  <div className="text-xs text-[#008080]">
                    <a
                      href={ipo.registrar.website.startsWith('http') ? ipo.registrar.website : `https://${ipo.registrar.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline flex items-center gap-1"
                    >
                      <span>{ipo.registrar.website}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>

              {/* Lead Manager (BRLM) */}
              {(ipo.leadManager || (ipo.leadManagerObj?.allLeadManagers && ipo.leadManagerObj.allLeadManagers.length > 0)) && (
                <div className="bg-white rounded-3xl border border-slate-200/90 p-5 space-y-2">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                    Book Running Lead Manager (BRLM)
                  </h4>
                  <div className="font-extrabold text-sm text-slate-900">
                    {ipo.leadManager || ipo.leadManagerObj?.allLeadManagers?.[0] || 'Lead Manager'}
                  </div>
                  {ipo.leadManagerObj?.contactPerson && (
                    <div className="text-xs text-slate-600">
                      Contact: {ipo.leadManagerObj.contactPerson}
                    </div>
                  )}
                  {ipo.leadManagerObj?.phone && (
                    <div className="text-xs text-slate-600">
                      Phone: {ipo.leadManagerObj.phone}
                    </div>
                  )}
                  {ipo.leadManagerObj?.email && (
                    <div className="text-xs text-slate-600">
                      Email: {ipo.leadManagerObj.email}
                    </div>
                  )}
                  {ipo.leadManagerObj?.allLeadManagers && ipo.leadManagerObj.allLeadManagers.length > 1 && (
                    <div className="pt-2 border-t border-slate-100">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        Syndicate Members ({ipo.leadManagerObj.allLeadManagers.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {ipo.leadManagerObj.allLeadManagers.map((mgr, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-lg bg-teal-50 text-teal-800 text-[10px] font-semibold"
                          >
                            {mgr}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Registered Office */}
              <div className="bg-white rounded-3xl border border-slate-200/90 p-5 space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">
                  Registered Office &amp; Contact
                </h4>
                {ipo.registeredOffice ? (
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {ipo.registeredOffice}
                  </p>
                ) : null}
                {ipo.officeContactPerson && (
                  <div className="text-xs text-slate-600">
                    Contact: {ipo.officeContactPerson}
                  </div>
                )}
                {ipo.contactPhone && (
                  <div className="text-xs text-slate-600">
                    Phone: {ipo.contactPhone}
                  </div>
                )}
                {ipo.contactEmail && (
                  <div className="text-xs text-slate-600">
                    Email: {ipo.contactEmail}
                  </div>
                )}
                {ipo.companyWebsite && (
                  <div className="text-xs text-[#008080]">
                    <a
                      href={ipo.companyWebsite.startsWith('http') ? ipo.companyWebsite : `https://${ipo.companyWebsite}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline flex items-center gap-1"
                    >
                      <span>{ipo.companyWebsite}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {!ipo.registeredOffice &&
                  !ipo.officeContactPerson &&
                  !ipo.contactPhone &&
                  !ipo.contactEmail &&
                  !ipo.companyWebsite && (
                    <p className="text-xs text-slate-400 italic">
                      Address available in DRHP
                    </p>
                  )}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: GMP TREND */}
        {/* ========================================================================= */}
        {activeTab === 'gmp' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                    <span>Grey Market Premium (GMP) Live Tracker</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Real-time unlisted primary market estimates based on institutional broker trades
                  </p>
                </div>
                {ipo.gmpUpdatedOn && (
                  <span className="text-[11px] font-semibold text-slate-400 bg-slate-50 border border-slate-200 px-3 py-1 rounded-full">
                    Updated: {ipo.gmpUpdatedOn}
                  </span>
                )}
              </div>

              {/* GMP Key Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                    Current GMP
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-emerald-700 mt-1">
                    {ipo.liveGmp || '—'}
                  </div>
                  <div className="text-xs font-bold text-emerald-600 mt-1">
                    {ipo.gmpPercent ? `${ipo.gmpPercent} Premium` : 'Neutral'}
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Day High / Low
                  </div>
                  <div className="text-lg sm:text-xl font-extrabold text-slate-900 mt-1">
                    {ipo.gmpDayHigh || '—'} / {ipo.gmpDayLow || '—'}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Intraday fluctuation
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Estimated Listing Price
                  </div>
                  <div className="text-lg sm:text-xl font-extrabold text-slate-900 mt-1">
                    {ipo.estimatedListingPrice || '—'}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {ipo.upperPrice ? `Cap Price: ₹${ipo.upperPrice}` : ''}
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Issue Status
                  </div>
                  <div className="text-lg sm:text-xl font-extrabold text-slate-900 mt-1">
                    {ipo.biddingStatus || 'Upcoming'}
                  </div>
                  <div className="text-[11px] text-[#008080] font-bold mt-1">
                    {ipo.exchanges || 'NSE, BSE'}
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-amber-50/50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                <strong>Disclaimer:</strong> Grey Market Premium (GMP) is an unregulated, unofficial indicative estimate based on off-market broker transactions. It is subject to extreme market volatility and should not be used as sole financial advice.
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: SUBSCRIPTION */}
        {/* ========================================================================= */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-slate-900">
                    Live Subscription Status (Exchange Total)
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Live institutional and retail bidding sync from NSE &amp; BSE
                  </p>
                </div>
                <div className="p-3 rounded-2xl bg-teal-50 border border-teal-200 text-right">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">
                    Overall Bidding
                  </span>
                  <div className="text-xl font-black text-[#008080]">
                    {ipo.overallSubscription || '—'}
                  </div>
                </div>
              </div>

              {/* Category-wise Subscription Table */}
              {ipo.subscriptionCategories && ipo.subscriptionCategories.length > 0 ? (
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Category</th>
                        <th className="py-2.5 px-4 text-right">Shares Offered</th>
                        <th className="py-2.5 px-4 text-right">Shares Bid</th>
                        <th className="py-2.5 pl-4 text-right">Subscription Times</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.subscriptionCategories.map((sub, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-3 pr-4 font-bold text-slate-900">
                            {sub.category}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-600">
                            {sub.shares_offered || '—'}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-600">
                            {sub.shares_bid || '—'}
                          </td>
                          <td className="py-3 pl-4 text-right font-extrabold text-[#008080]">
                            {sub.times || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-500 space-y-2">
                  <div className="font-bold text-slate-700">
                    Subscription Data Opens with Bidding
                  </div>
                  <p className="max-w-md mx-auto">
                    Live category-wise (QIB, NII, Retail) bidding figures will be streamed directly from exchange feeds once bidding opens on {ipo.openDate || 'the start date'}.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: FINANCIALS & VALUATION */}
        {/* ========================================================================= */}
        {activeTab === 'financials' && (
          <div className="space-y-6">
            {/* Restated Financial History Table */}
            <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-slate-900">
                    Financial Performance
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Amounts in {ipo.financialUnit || '₹ Lakhs'}
                  </p>
                </div>
                {ipo.revenueCagr && (
                  <span className="text-xs font-bold px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                    CAGR: {ipo.revenueCagr}
                  </span>
                )}
              </div>

              {ipo.financialRows && ipo.financialRows.length > 0 ? (
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Fiscal Period</th>
                        <th className="py-2.5 px-4 text-right">Revenue</th>
                        <th className="py-2.5 pl-4 text-right">Profit After Tax (PAT)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.financialRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-3 pr-4 font-bold text-slate-900">
                            {row.year}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-700">
                            {row.revenue || '—'}
                          </td>
                          <td className="py-3 pl-4 text-right font-extrabold text-emerald-600">
                            {row.pat || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-2xl">
                  Restated financial data will appear here once extracted from the audit report.
                </div>
              )}
            </div>

            {/* Year-on-Year Financial KPIs Matrix */}
            {ipo.kpisMatrix && ipo.kpisMatrix.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#008080]" />
                  <span>Key Operating Margins &amp; Ratios</span>
                </h3>
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Metric</th>
                        {ipo.kpiYears?.map((fy, idx) => (
                          <th key={idx} className="py-2.5 px-4 text-right">
                            {fy.toUpperCase().replace('_', ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.kpisMatrix.map((kpi, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-3 pr-4 font-bold text-slate-800">
                            {kpi.label}
                          </td>
                          {kpi.values.map((v, vIdx) => (
                            <td
                              key={vIdx}
                              className="py-3 px-4 text-right font-semibold text-emerald-700"
                            >
                              {v.value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Historical EPS Table (if available) */}
            {ipo.epsList && ipo.epsList.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#008080]" />
                  <span>Earnings Per Share (EPS) History</span>
                </h3>
                <div className="overflow-x-auto scrollbar-slim">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 text-[11px] uppercase tracking-wider">
                        <th className="py-2.5 pr-4">Fiscal Year</th>
                        <th className="py-2.5 px-4 text-right">Basic EPS</th>
                        <th className="py-2.5 pl-4 text-right">Diluted EPS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ipo.epsList.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="py-3 pr-4 font-bold text-slate-900">
                            {row.fiscal_year || `Year ${idx + 1}`}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-700">
                            {row.basic_eps ? `₹${row.basic_eps}` : '—'}
                          </td>
                          <td className="py-3 pl-4 text-right font-semibold text-slate-700">
                            {row.diluted_eps ? `₹${row.diluted_eps}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Key Valuation Ratios & Metrics */}
            <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-4">
              <h3 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#008080]" />
                <span>Valuation Multiples &amp; Capitalisation</span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    P/E (Issue Cap)
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                    {ipo.peAtIssuePrice || ipo.derivedValuation?.pe_at_issue_price || '—'}
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    P/E (Floor)
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                    {ipo.peAtFloorPrice || '—'}
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    NAV Per Share
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                    {ipo.navPerShare ? `₹${ipo.navPerShare}` : '—'}
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    Weighted RoNW
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-emerald-700 mt-1">
                    {ipo.weightedAvgRonw || '—'}
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    Pre-Issue M-Cap
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                    {ipo.mcapPreIssue || '—'}
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-bold text-slate-400 uppercase">
                    Post-Issue M-Cap
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-[#008080] mt-1">
                    {ipo.mcapPostIssue || (ipo.derivedValuation?.market_cap_post_issue_cr ? `₹${ipo.derivedValuation.market_cap_post_issue_cr} Cr` : '—')}
                  </div>
                </div>
              </div>

              {/* Industry Peer P/E Benchmarks */}
              {(ipo.peComposite || ipo.peLowest || ipo.peHighest) && (
                <div className="p-4 rounded-2xl bg-teal-50/50 border border-teal-100/80 flex flex-wrap items-center justify-between gap-4 text-xs">
                  <span className="font-bold text-teal-900">Industry Peer P/E Range:</span>
                  <div className="flex items-center gap-4">
                    {ipo.peLowest && (
                      <span className="text-slate-600">
                        Lowest: <strong className="text-slate-900">{ipo.peLowest}</strong>
                      </span>
                    )}
                    {ipo.peComposite && (
                      <span className="text-slate-600">
                        Composite: <strong className="text-teal-800">{ipo.peComposite}</strong>
                      </span>
                    )}
                    {ipo.peHighest && (
                      <span className="text-slate-600">
                        Highest: <strong className="text-slate-900">{ipo.peHighest}</strong>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: NEWS */}
        {/* ========================================================================= */}
        {activeTab === 'news' && <CompanyNewsPanel company={ipo.companyName} news={ipo.companyNews} />}

        {/* IPO detail in-content ad (GET /api/ads -> 'incontent') */}
        <AdSlot slotId="incontent" className="mt-10 sm:mt-12" />

        {/* Frequently Asked Questions Section */}
        <div className="mt-12 sm:mt-16 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Frequently Asked Questions
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Key answers on {ipo.companyName} subscription, eligibility, and process
            </p>
          </div>

          <div className="space-y-3" aria-busy={faqsLoading}>
            {faqsLoading &&
              Array.from({ length: 4 }).map((_, idx) => (
                <div key={`faq-skeleton-${idx}`} className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 animate-pulse">
                  <div className="h-4 w-3/4 bg-slate-200 rounded" />
                  {idx === 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="h-3 w-full bg-slate-100 rounded" />
                      <div className="h-3 w-5/6 bg-slate-100 rounded" />
                    </div>
                  )}
                </div>
              ))}
            {!faqsLoading && faqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              const panelId = `ipo-faq-${faq.id || idx}`;
              return (
                <div
                  key={faq.id || idx}
                  className="bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-xs transition-all"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-slate-900 hover:text-[#008080] transition-colors cursor-pointer"
                  >
                    <span>{faq.question}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#008080]' : ''
                        }`}
                    />
                  </button>
                  <div
                    id={panelId}
                    hidden={!isOpen}
                    className="px-4 sm:px-5 pb-5 pt-0 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-50"
                  >
                    {faq.answer}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Real-time Alert Signup Strip */}
        {/* <div className="mt-12 bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center text-[#008080] shrink-0">
                <Sparkles className="w-5 h-5 text-[#008080]" />
              </div>
              <div>
                <h4 className="font-bold text-sm sm:text-base text-slate-900">
                  Set Daily IPO GMP &amp; Subscription Alerts
                </h4>
                <p className="text-xs text-slate-500">
                  Receive updates directly on your WhatsApp or inbox every morning before market opening.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 max-w-md w-full md:w-auto">
              <input
                type="text"
                placeholder="Enter WhatsApp number or Email..."
                className="flex-1 px-4 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080]"
              />
              <button
                type="button"
                className="px-5 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-white text-xs font-bold shrink-0 transition-colors cursor-pointer"
              >
                Get Alerts →
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default IpoDetailsPage;
